// useCaptchaForm.js
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import useGeeTestVerification from '../hook/useGeeTestVerification.js';
import TurnstileDisplay from '../component/TurnstileDisplay.jsx';
import { SET_OPERATION_DATA } from '../ActionTypes.js';
import pushToastr from '../action/pushToastr.js';
import env from '../resource/env.js';
import { getCaptchaProvider } from '../selector/getCaptchaHeaders.js';
import getIsCaptchaVerified from '../selector/getIsCaptchaVerified.js';
import getOperationData from '../selector/getOperationData.js';
import getIsRemoteConfigMerged from '../selector/getIsRemoteConfigMerged.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import { CAPTCHA_LOADING_TIMEOUT_SEC } from '../RemoteConfigKeys.js';
import {
  CONFIG_PRIORITY_PRESENCE_CLIENT,
  CONFIG_PRIORITY_PRESENCE_USER,
} from '../resource/configPriority.js';
import getMeData from '../selector/getMeData.js';
import { captcha } from '../resource/debug.js';
import { EMPTY_OBJECT, NULL_FUNCTION } from '../resource/defaults.js';

const log = captcha.extend('log:useCaptchaForm');

/**
 * Unified Hook for form submission with CAPTCHA verification
 * Handles all CAPTCHA providers (GeeTest, Turnstile, none) in one Hook
 * No need to update Container - all selectors are handled internally
 *
 * Flow:
 * 1. Component calls startVerification()
 * 2. Hook triggers CAPTCHA verification based on provider (GeeTest popup / Turnstile)
 * 3. On success → onVerificationSuccess callback is called
 *    On error → error is logged
 *
 * @kind hook
 * @param {Object} options - Configuration options
 * @param {Function} options.onVerificationSuccess - Callback function to execute after successful verification
 * @return {Object} { startVerification, CaptchaWidget }
 *
 * @example
 * const { startVerification, CaptchaWidget } = useCaptchaForm({
 *   onVerificationSuccess: handleSendOtp
 * });
 *
 * return (
 *   <form>
 *     {CaptchaWidget}
 *     <button onClick={startVerification}>Submit</button>
 *   </form>
 * );
 */
const useCaptchaForm = (options = EMPTY_OBJECT) => {
  const { onVerificationSuccess = NULL_FUNCTION } = options;

  const dispatch = useDispatch();
  const captchaProvider = useSelector(state => getCaptchaProvider(state));
  const isCaptchaVerified = useSelector(state => getIsCaptchaVerified(state));
  const isWaitingForCaptcha = useSelector(state =>
    getOperationData(state, ['captcha'], 'isWaitingForCaptcha')
  );
  // Use the highest available priority to check if remote config is loaded
  // - Logged in: use Priority 2 (PRESENCE_USER), always reloaded via handleLoginToken
  // - Logged out: use Priority 1 (PRESENCE_CLIENT), reloaded via resetConfigurations
  // Previously only checked Priority 1, but OTP/OAuth login dispatches LOGOUT
  // which deletes remoteConfigMerged without reloading Priority 1
  const hasToken = useSelector(state => !!getMeData(state, 'token'));
  const configPriority = hasToken
    ? CONFIG_PRIORITY_PRESENCE_USER
    : CONFIG_PRIORITY_PRESENCE_CLIENT;
  const isRemoteConfigMerged = useSelector(state =>
    getIsRemoteConfigMerged(state, configPriority)
  );

  // If CAPTCHA loading takes too long show an error toast.
  const captchaLoadingTimeoutSec =
    useSelector(state =>
      getRemoteConfigData(state, CAPTCHA_LOADING_TIMEOUT_SEC)
    ) || env.CAPTCHA_LOADING_TIMEOUT_SEC;

  log('[useCaptchaForm] init', {
    captchaProvider,
    isCaptchaVerified,
    isWaitingForCaptcha,
    isRemoteConfigMerged,
  });

  const [showTurnstileError, setShowTurnstileError] = useState(false);

  // Track pending verification when waiting for remote config
  const hasPendingVerificationRef = useRef(false);
  const captchaLoadingTimeoutRef = useRef(null);

  const clearCaptchaLoadingTimeout = useCallback(() => {
    if (captchaLoadingTimeoutRef.current) {
      clearTimeout(captchaLoadingTimeoutRef.current);
      captchaLoadingTimeoutRef.current = null;
      log('[useCaptchaForm] captcha loading timeout cleared');
    }
  }, []);

  // GeeTest verifications
  const { triggerGeeTestVerification, resetGeeTestInstance } =
    useGeeTestVerification({
      onVerificationSuccess,
      onCaptchaLoadEnd: clearCaptchaLoadingTimeout,
    });

  // Execute verification based on provider type
  const executeVerification = useCallback(async () => {
    log('[executeVerification] captchaProvider:', captchaProvider);

    // Set loading state
    dispatch({
      type: SET_OPERATION_DATA,
      payload: {
        selectPath: ['captcha'],
        data: { isWaitingForCaptcha: true },
      },
    });

    try {
      if (captchaProvider === 'geetest') {
        log('[executeVerification] Triggering GeeTest');
        await triggerGeeTestVerification();
        return;
      }

      if (captchaProvider === 'turnstile') {
        if (isCaptchaVerified) {
          log('[executeVerification] Turnstile verified');
          onVerificationSuccess();
        } else {
          log('[executeVerification] Turnstile not verified');
          setShowTurnstileError(true);
        }
        return;
      }

      // No CAPTCHA required (remote config is loaded and no CAPTCHA provider is configured)
      log('[executeVerification] No CAPTCHA required');
      onVerificationSuccess();
    } finally {
      // Clear loading state for all providers
      dispatch({
        type: SET_OPERATION_DATA,
        payload: {
          selectPath: ['captcha'],
          data: { isWaitingForCaptcha: false },
        },
      });
    }
  }, [
    captchaProvider,
    isCaptchaVerified,
    dispatch,
    triggerGeeTestVerification,
    onVerificationSuccess,
  ]);

  // when remote config loaded and has pending verification
  // trigger CAPTCHA verification
  useEffect(() => {
    if (hasPendingVerificationRef.current && isRemoteConfigMerged) {
      log('[useEffect] Config loaded, executing pending verification');
      hasPendingVerificationRef.current = false;
      executeVerification();
    }
  }, [isRemoteConfigMerged, executeVerification]);

  useEffect(() => {
    return () => {
      clearCaptchaLoadingTimeout();
    };
  }, [clearCaptchaLoadingTimeout]);

  // Main verification entry point
  const startVerification = useCallback(async () => {
    // Start timeout to show error toast if CAPTCHA loading takes too long.
    captchaLoadingTimeoutRef.current = setTimeout(() => {
      log('[startVerification] captcha loading timeout');
      dispatch(
        pushToastr({
          textKey: 'system_processing_wait_or_try_later',
          attachedMessage: isRemoteConfigMerged ? '(SDK_INIT)' : '(CONFIG)',
          color: 'error',
          durationSec: 10,
        })
      );
    }, captchaLoadingTimeoutSec * 1000);

    // Remote config not loaded yet - set pending and show loading
    if (!isRemoteConfigMerged) {
      log('[startVerification] Waiting for remote config...');
      hasPendingVerificationRef.current = true;
      dispatch({
        type: SET_OPERATION_DATA,
        payload: {
          selectPath: ['captcha'],
          data: { isWaitingForCaptcha: true },
        },
      });
      return;
    }

    // Config loaded - execute immediately
    await executeVerification();
  }, [
    isRemoteConfigMerged,
    captchaLoadingTimeoutSec,
    dispatch,
    executeVerification,
  ]);

  // Widget (only Turnstile needs visible widget)
  const CaptchaWidget = useMemo(() => {
    if (captchaProvider === 'turnstile') {
      return <TurnstileDisplay showErrorText={showTurnstileError} />;
    }
    return null;
  }, [captchaProvider, showTurnstileError]);

  return {
    startVerification,
    resetCaptcha:
      captchaProvider === 'geetest' ? resetGeeTestInstance : NULL_FUNCTION,
    CaptchaWidget,
    isWaitingForCaptcha: !!isWaitingForCaptcha,
  };
};

export default useCaptchaForm;
